import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { fetchContactInfo, type ContactInfo } from "../../api";

export default function About() {
  const [info, setInfo] = useState<ContactInfo | null>(null);

  useEffect(() => {
    fetchContactInfo()
      .then(setInfo)
      .catch((err) => console.warn("联系信息加载失败:", err));
  }, []);
  const advantages = [
    {
      icon: "ri-shield-check-line",
      title: "Rugged & Reliable",
      desc: "Our devices are built to withstand extreme temperatures, dust, moisture, and impacts — meeting IP67 and MIL-STD-810G standards.",
    },
    {
      icon: "ri-signal-tower-line",
      title: "Crystal-Clear Audio",
      desc: "Advanced noise-cancellation and digital voice processing ensure every word is heard loud and clear, even in the noisiest environments.",
    },
    {
      icon: "ri-tools-line",
      title: "Custom Solutions",
      desc: "From OEM/ODM design to firmware customization, we tailor every product to meet the specific demands of your industry.",
    },
    {
      icon: "ri-global-line",
      title: "Global Reach",
      desc: "With distribution across 40+ countries and multilingual support, we deliver wherever your teams operate.",
    },
  ];

  const values = [
    {
      icon: "ri-lightbulb-flash-line",
      title: "Innovation First",
      desc: "We invest 15% of revenue annually into R&D, continuously pushing the boundaries of wireless communication technology.",
    },
    {
      icon: "ri-user-smile-line",
      title: "Customer Obsession",
      desc: "Your mission-critical operations deserve nothing less than our full attention. We provide 24/7 support and fast-turnaround service.",
    },
    {
      icon: "ri-plant-line",
      title: "Sustainable Future",
      desc: "We are committed to eco-friendly manufacturing, recyclable packaging, and energy-efficient product designs.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20technology%20company%20office%20interior%20with%20team%20working%20collaborative%20workspace%20blue%20and%20green%20color%20accents%20natural%20light%20professional%20photography%20high%20quality%20corporate%20environment&width=1400&height=800&seq=34&orientation=landscape"
              alt="Huatera office"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">About Us</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Building the Future of<br />
                <span className="text-secondary">Communication</span>
              </h1>
            </div>
          </div>
        </section>

        {/* Company Profile */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
                Company Profile
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Quanzhou Shijia Rongyan Electronic Technology Co, Ltd. is a professional manufacturer and supplier of two-way radios and wireless communication equipment. Headquartered in Nan&apos;an City, Fujian Province, we have built a strong reputation for delivering reliable, high-performance communication solutions to clients worldwide.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                With years of experience in the wireless communication industry, we specialize in the research, development, production, and sales of professional walkie-talkies, radio accessories, and customized communication systems. Our products serve a wide range of industries including security, construction, logistics, hospitality, and public safety.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We are committed to quality, innovation, and customer satisfaction. Every product undergoes rigorous testing to ensure durability, clear audio, and long-lasting performance — even in the most demanding environments.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20industrial%20factory%20building%20exterior%20in%20China%20with%20large%20glass%20windows%20and%20clean%20architectural%20design%2C%20surrounded%20by%20green%20landscaping%2C%20professional%20corporate%20headquarters%20manufacturing%20facility%2C%20bright%20daylight%2C%20high%20quality%20photography%2C%20clean%20and%20professional%20look&width=800&height=600&seq=55&orientation=squarish"
                alt="Huatera factory"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Company Info */}
        <section className="bg-light-gray py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                Company Information
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Quanzhou Shijia Rongyan Electronic Technology Co, Ltd. — your trusted partner in professional wireless communication
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Company Name</h3>
                      <p className="text-gray-600 text-sm">{info?.company_name || "Quanzhou Shijia Rongyan Electronic Technology Co, Ltd."}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Mobile Phone</h3>
                      <p className="text-gray-600 text-sm">{info?.company_phone || "+86 137 8885 5889"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Email</h3>
                      <p className="text-gray-600 text-sm">{info?.company_email || "huatera88@gmail.com"}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Address</h3>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{info?.company_address || "Gangwu (Chenghui International)\nQiuzhong Village, Xiamei Town\nNan'an City, Fujian Province"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Website</h3>
                      <p className="text-gray-600 text-sm">www.huatera.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="bg-light-blue py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                Our Vision
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                To be the world's most trusted partner in professional communication, empowering every industry to work safer, smarter, and more connected.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-6 md:p-8 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                    <i className={`${item.icon} text-primary text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                Why Choose Huatera
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                We combine cutting-edge engineering with real-world experience to deliver communication tools that simply work.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((item, index) => (
                <div key={index} className="bg-light-gray rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                    <i className={`${item.icon} text-primary text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}